/**
 * Callback del login Microsoft Entra ID (TC_AUTH_PROVIDER=entra).
 *
 * Route pubblica `/auth/entra/callback` (Redirect URI "SPA" registrato in Entra): oidc-client-ts
 * completa code → token, il product-service scambia l'id_token con il JWT applicativo (entra.ts).
 * Successo → pagina richiesta prima del login; errore → messaggio e link al login.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, completeEntraLogin, getToken } from '../auth/index.js';
import styles from './LoginPage.module.css';

/** Messaggio leggibile per gli errori restituiti da Entra nella query (?error=…). */
function entraErrorFromQuery(search: string): string | null {
  const params = new URLSearchParams(search);
  const error = params.get('error');
  if (!error) return null;
  const description = params.get('error_description');
  return `Microsoft Entra ID ha rifiutato l'accesso (${error})${description ? `: ${description}` : ''}`;
}

export function EntraCallbackPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // StrictMode esegue gli effetti due volte: la callback è one-shot (state consumato al primo run).
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const fromQuery = entraErrorFromQuery(window.location.search);
    if (fromQuery) {
      setError(fromQuery);
      return;
    }

    void (async () => {
      try {
        const returnTo = await completeEntraLogin();
        const token = getToken();
        if (!token) throw new Error('Token non salvato dopo lo scambio.');
        login(token);
        navigate(returnTo, { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Accesso Microsoft non riuscito.');
      }
    })();
  }, []);

  return (
    <div className={styles.page} data-testid="entra-callback-page">
      <div className={styles.card}>
        <h1 className={styles.brand}>
          Tenacious<span className={styles.brandAccent}>C</span>
        </h1>
        {error ? (
          <>
            <p className={styles.error} role="alert" data-testid="entra-error">
              {error}
            </p>
            <a href="/login">Torna al login</a>
          </>
        ) : (
          <p className={styles.hint}>Accesso in corso…</p>
        )}
      </div>
    </div>
  );
}
